import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { BranchProvider } from "@/context/BranchContext";
import WorkspaceLayout from "@/components/WorkspaceLayout";
import AdminLayout from "@/components/AdminLayout";
import Login from "@/pages/Login";
import ScheduleBoard from "@/pages/ScheduleBoard";
import DataTable from "@/pages/DataTable";
import AccountingBoard from "@/pages/AccountingBoard";
import Analytics from "@/pages/Analytics";
import StaffManagement from "@/pages/StaffManagement";
import ClientsManagement from "@/pages/ClientsManagement";
import ServiceManagement from "@/pages/ServiceManagement";
import Payroll from "@/pages/Payroll";
import Roster from "@/pages/Roster";
import CashFlowReport from "@/pages/CashFlowReport";
import AdminSettings from "@/pages/AdminSettings";
import Support from "@/pages/Support";
import { Loader2 } from "lucide-react";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-forest" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PermissionRoute({ children, permission }) {
  const { hasPermission, user } = useAuth();
  if (user?.role === "admin") return children;
  if (permission && !hasPermission(permission)) return <Navigate to="/support" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (user?.role !== "admin") return <Navigate to="/worksheet" replace />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-forest" />
      </div>
    );
  }
  if (user) return <Navigate to="/worksheet" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />

      {/* Frontline operational workspace — 3-tab shell */}
      <Route element={<ProtectedRoute><WorkspaceLayout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/schedule" replace />} />
        <Route path="schedule" element={<PermissionRoute permission="schedule"><ScheduleBoard /></PermissionRoute>} />
        <Route path="data" element={<PermissionRoute permission="operations"><DataTable /></PermissionRoute>} />
        <Route path="accounting" element={<PermissionRoute permission="accounting"><AccountingBoard /></PermissionRoute>} />
        <Route path="support" element={<Support />} />
        {/* Legacy redirects */}
        <Route path="worksheet" element={<Navigate to="/schedule" replace />} />
        <Route path="operations" element={<Navigate to="/data" replace />} />
        <Route path="booking" element={<Navigate to="/schedule" replace />} />
      </Route>

      {/* Admin console / backend management */}
      <Route path="/admin" element={<ProtectedRoute><AdminOnly><AdminLayout /></AdminOnly></ProtectedRoute>}>
        <Route index element={<Navigate to="/admin/accounts" replace />} />
        <Route path="accounts" element={<StaffManagement />} />
        <Route path="clients" element={<ClientsManagement />} />
        <Route path="services" element={<ServiceManagement />} />
        <Route path="payroll" element={<Payroll />} />
        <Route path="roster" element={<Roster />} />
        <Route path="accounting" element={<Navigate to="/admin/settings" replace />} />
        <Route path="cashflow" element={<CashFlowReport />} />
        <Route path="safe" element={<Navigate to="/admin/cashflow" replace />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="settings" element={<AdminSettings />} />
      </Route>

      {/* Legacy top-level redirects to admin console */}
      <Route path="/staff" element={<Navigate to="/admin/accounts" replace />} />
      <Route path="/services" element={<Navigate to="/admin/services" replace />} />
      <Route path="/payroll" element={<Navigate to="/admin/payroll" replace />} />
      <Route path="/roster" element={<Navigate to="/admin/roster" replace />} />
      <Route path="/reports" element={<Navigate to="/admin/cashflow" replace />} />
      <Route path="/safe" element={<Navigate to="/admin/cashflow" replace />} />
      <Route path="/analytics" element={<Navigate to="/admin/analytics" replace />} />
      <Route path="/settings" element={<Navigate to="/admin/settings" replace />} />

      <Route path="*" element={<Navigate to="/worksheet" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="min-h-screen">
      <BrowserRouter>
        <AuthProvider>
          <BranchProvider>
            <AppRoutes />
            <Toaster richColors position="top-right" />
          </BranchProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
